import ResponsiveContainer from './ResponsiveContainer.jsx';
import { ChartTooltip, useChartHover } from './ChartTooltip.jsx';
import { bandScale, linearScale, niceTicks } from './scale.js';
import { SERIES, FONT, LABEL, softFill } from './chartColors.js';
import Axis from './Axis.jsx';

// T239/T259 — a rate over time. The kit's second chart type.
//
// Deliberately NOT a generic line chart: it knows about PROVISIONAL points.
// A conversion rate measured over recent cohorts is right-censored — the newest
// applications have not had time to be answered — so plotting them like settled
// history makes every such chart slope down at the right edge and reads as a
// collapse. `matureOf` marks where the settled data ends: up to there the line
// is solid with an area under it, past it dashed and hollow, and the split is
// named in the legend rather than left to be inferred.
//
// Accessors receive (datum, index):
//   x        — category label (also the tooltip/aria name)
//   y        — the rate, in the same units as domainMax (default 0..100)
//   matureOf — false → this point is provisional
//   thinOf   — true → too few samples for the rate to mean anything; drawn as a
//              hollow ring with no line through it, and named as such
//   nOf      — optional denominator, folded into the tooltip and aria label
export default function LineChart({
  data = [],
  x = (d) => d.label,
  y = (d) => d.value,
  matureOf,
  thinOf,
  nOf,
  domainMax = 100,
  yTickText = (t) => `${t}%`,
  height = 160,
  color = SERIES.accent,
  ariaLabel,
}) {
  const { hover, show, hide } = useChartHover();
  if (!data.length) return null;

  const ticks = niceTicks(domainMax, 4);
  const max = ticks[ticks.length - 1];
  const padLeft = 30;
  const padTop = 8;
  const padBottom = 18;

  return (
    <div className="relative">
      <ResponsiveContainer height={height}>
        {(w, h) => {
          const plotW = Math.max(2, w - padLeft);
          const plotH = Math.max(2, h - padTop - padBottom);
          const band = bandScale(data.length, plotW, { padding: 0 });
          const scaleY = linearScale(max, plotH);
          const yOf = (v) => padTop + plotH - scaleY(Math.max(0, Math.min(max, v ?? 0)));
          const xOf = (i) => padLeft + band.x(i) + band.bandWidth / 2;

          const pt = (i) => `${xOf(i)},${yOf(y(data[i], i))}`;
          const thin = (i) => Boolean(thinOf?.(data[i], i));
          const mature = (i) => (matureOf ? Boolean(matureOf(data[i], i)) : true);

          // The line is drawn through READABLE points only. A rate computed
          // from one or two applications is not a trend, and connecting through
          // it lets two data points draw the steepest move on the chart — the
          // operator's own history opens with a 0/1 week and a 1/1 week, which
          // rendered as a confident fall from 100% to 0%. Thin weeks still get
          // a marker, so nothing is hidden; they just carry no slope. The runs
          // below are that filter — a flat list of readable indices was kept
          // alongside them and read by nothing.

          // Drawing through the thin weeks is not the same as skipping them: a
          // straight segment from week 1 to week 3 passes over week 2's column
          // at an interpolated height, which is a value that week did not have.
          // So the line BREAKS at a thin week instead of bridging it — a gap
          // says "unknown", a bridge says something false.
          const runs = [];
          let run = [];
          for (let i = 0; i < data.length; i++) {
            if (thin(i)) {
              if (run.length) runs.push(run);
              run = [];
            } else {
              run.push(i);
            }
          }
          if (run.length) runs.push(run);

          // Within each unbroken run, split at the maturity boundary. (The
          // whole-series settled/provisional split that used to be computed here
          // was never read — `s` and `p` per run are what the segments use.)
          const segments = runs.flatMap((r) => {
            const s = r.filter(mature);
            const p = r.filter((i) => !mature(i));
            const out = [];
            if (s.length > 1) out.push({ pts: s, dashed: false });
            // Join the dashed part to the settled part it continues from.
            const tail = s.length ? [s[s.length - 1], ...p] : p;
            if (tail.length > 1) out.push({ pts: tail, dashed: true });
            return out;
          });
          // The area only ever sits under settled, unbroken history.
          const areaRuns = runs.map((r) => r.filter(mature)).filter((r) => r.length > 1);

          return (
            <>
              <svg width={w} height={h} role="group" aria-label={ariaLabel} style={{ display: 'block', fontFamily: FONT }}>
                {/* T341 — the shared axis. */}
                <Axis ticks={ticks} yOf={yOf} padLeft={padLeft} width={w} tickText={yTickText} />

                {areaRuns.map((r, k) => (
                  <polygon
                    key={`a${k}`}
                    points={`${xOf(r[0])},${yOf(0)} ${r.map(pt).join(' ')} ${xOf(r[r.length - 1])},${yOf(0)}`}
                    fill={softFill(color, 18)}
                  />
                ))}
                {segments.map((seg, k) => (
                  <polyline
                    key={`s${k}`}
                    points={seg.pts.map(pt).join(' ')}
                    fill="none"
                    stroke={color}
                    strokeWidth="2"
                    strokeDasharray={seg.dashed ? '4 3' : undefined}
                  />
                ))}

                {data.map((d, i) => {
                  const isMature = mature(i);
                  const isThin = thin(i);
                  const n = nOf?.(d, i);
                  const label =
                    `${x(d, i)}: ${y(d, i) ?? 0}%${n != null ? ` (${n} sent)` : ''}` +
                    `${isThin ? ' — too few to read as a rate' : ''}${isMature ? '' : ' — still coming in'}`;
                  const enter = () => show(xOf(i), yOf(y(d, i)), label);
                  return (
                    <g key={i}>
                      <circle
                        cx={xOf(i)}
                        cy={yOf(y(d, i))}
                        r="3.5"
                        fill={isMature && !isThin ? color : 'var(--c-surface-1)'}
                        // TRACK is a FILL token — an area colour. As the only
                        // ink on a 3.5px ring it measured 1.06:1 against the
                        // card on the default light theme (a white disc ringed
                        // in near-white) and never beat 1.16:1 on any of the
                        // seven, so the "thin weeks still get a marker" promise
                        // rendered as two labelled columns with nothing above
                        // them. LABEL is 6.2:1 at worst. The missing LINE is
                        // what distinguishes thin from provisional; the marker
                        // does not also have to be faint to make the point.
                        stroke={isThin ? LABEL : color}
                        strokeWidth="1.5"
                        tabIndex={0}
                        role="img"
                        aria-label={label}
                        style={{ cursor: 'default' }}
                        onMouseEnter={enter}
                        onMouseLeave={hide}
                        onFocus={enter}
                        onBlur={hide}
                      />
                      <text x={xOf(i)} y={h - 4} textAnchor="middle" fontSize="9" fill={LABEL}>
                        {x(d, i)}
                      </text>
                    </g>
                  );
                })}
              </svg>
              <ChartTooltip hover={hover} width={w} />
            </>
          );
        }}
      </ResponsiveContainer>
    </div>
  );
}
